import React, { useCallback } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import type { DocListResponse } from '../../openapi/types';
import type { RouteId } from '../../routes';
import { useAppContext, useHistoryAction } from '../../hooks';
import { getCmdCtrlUrl } from '../../utils/getcmdctrlurl';
import { checkSourceEnum } from '../constants';

import { CheckBadge } from './CheckBadge';
import { AttachmentIcon } from './AttachmentIcon';
import { DocBadge } from './DocBadge';
import { checkSourceMessages } from './checkMessages';

export type CheckListItemProps = {
  data: DocListResponse;
  workType?: 'PREPARING';
};

export const CheckListItem: React.FC<CheckListItemProps> = ({ data, workType }) => {
  let badge;
  let routeDetail: RouteId = 'docCheck';
  switch (workType) {
    case 'PREPARING': {
      badge = (
        <DocBadge
          docState={data.state}
          flow={data.flow}
          rejectedBySubjekty={data.rejected_by_subjekty}
        />
      );
      routeDetail = 'docDetail';
      break;
    }
    default: {
      // na zaklade provolani R17 kdy selze - NOTIFYING_CLOSE_FAILED, musi se vizualizovat podle zadani dle state a ne check state
      // - muze nastat i diky chybe podepisovani
      // - z duvodu zmeny vizualizace nemuzeme pouzivat puvodni state z API, jelikoz je to jiny typ datoveho prenosu
      if (data.state === 'NOTIFYING_CLOSE_FAILED') {
        badge = <DocBadge
          docState={data.state}
          flow={data.flow}
          rejectedBySubjekty={data.rejected_by_subjekty}
        />;
      }
      else {
        badge = <CheckBadge state={data.latest_check?.state} />;
      }
    }
  }
  const { appType } = useAppContext();
  const routeToCheckDetail = useHistoryAction(routeDetail, { appType, id: data.id });

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(getCmdCtrlUrl(routeDetail as RouteId, { appType, id: data.id }), '_blank');
        return; // Ponech nativní chování
      }

      const selectText = window.getSelection();
      if (!selectText || selectText.toString() === '') {
        routeToCheckDetail();
      }
    },
    [routeToCheckDetail],
  );

  return (
    <tr onClick={handleClick}>
      <td className='sg-a-ta-r sg-a-fvn-tn'>{data.id}</td>
      <td>{badge}</td>
      <td className='sg-u-type-bold'>{data.name}</td>
      <td className='sg-a-ws-nw'>
        <FormattedMessage
          {...checkSourceMessages[
          data.dmvs_source ? checkSourceEnum.ISDMVS : checkSourceEnum.ISDTMK
          ]}
        />
      </td>
      <td>{data.dmvs_id_podani || '-'}</td>
      <td className='sg-a-ws-nw'>
        {data.created && (
          <>
            <FormattedDate value={new Date(data.created)} />{' '}
            <FormattedTime value={new Date(data.created)} />
          </>
        )}
      </td>
      <td>
        <div>{`${data.creator?.name ?? ''} ${data.creator?.surname ?? ''}`.trim()}</div>
        <div className='dmx-u-c-cta-gray'>
          {`${data.geodesist?.name ?? ''} ${data.geodesist?.surname ?? ''}`.trim()}
        </div>
      </td>
      <td className='sg-a-w-0'>
        {data.attachments_count > 0 && <AttachmentIcon count={data.attachments_count} />}
      </td>
    </tr>
  );
};
